import { EditModel } from 'edit/EditModel';
import { waitDialog } from 'dialogs';
import { postZone } from 'api/zones/endpoints/postZone';
import { putZone } from 'api/zones/endpoints/putZone';
import type { Zone } from 'api/zones/endpoints/postZone';
import { loadZone } from 'api/zones/operations/loadZone';
import { EditableZone } from './EditableZone';
import { EditZoneDialog } from './EditZoneDialog';

export type ShowEditZoneArgs = { readonly mode: 'create' } | { readonly mode: 'edit'; readonly name: string };

// Persist: POST when new, PUT (addressed by the pre-edit name) when editing.
const save = (entity: EditableZone): Promise<Zone> => {
  const body = entity.toSave();
  const original = entity.original;
  return original === null ? postZone(body) : putZone(original.name, body);
};

/**
 * Open the zone create / edit dialog and wait for the user to save or cancel.
 * Resolves to `true` if a save succeeded (caller should refresh its list),
 * `undefined` if cancelled. For edit it loads the record by name
 * (boundary-validated); a missing record throws, surfaced inline by the dialog.
 */
export const showEditZone = (args: ShowEditZoneArgs): Promise<true | undefined> => {
  const initialize: () => Promise<Zone | null> =
    args.mode === 'create'
      ? async () => null
      : async () => {
          const zone = await loadZone(args.name);
          if (!zone) throw new Error('Zone not found; the list may be out of date. Please refresh.');
          return zone;
        };

  const model = new EditModel(initialize, save, (loaded) => new EditableZone(loaded), {
    initialMode: args.mode === 'edit' ? 'edit' : 'create',
    successMessage: args.mode === 'edit' ? 'Zone updated' : 'Zone created',
  });

  return waitDialog<true>((ok, cancel) => <EditZoneDialog model={model} onSaved={() => ok(true)} onClose={cancel} />);
};
